import { CheckOutlined, ClockCircleOutlined, FileTextOutlined, RightOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import ModuleCard from 'components/cards/ModuleCard';
import posthog from 'posthog-js';

import { Button } from 'components/ui';
import { colors } from 'design';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { QuestionnaireResponseDTO } from 'services/api/models/QuestionnaireDTO';
import { routes } from 'services/RouteService';

const useStyles = createUseStyles({
  notStartedContent: {
    paddingTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  notStartedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 14,
  },
  notStartedIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  notStartedIconSize: {
    fontSize: 20,
    color: colors.brand.onAccent,
  },
  notStartedBadgesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  notStartedBadge: {
    backgroundColor: colors.brand.primarySubtle,
    /*
     * `.fg`, not `.solid`. That distinction is the whole point of the feedback
     * sets: `solid` is a fill and `fg` is the same status at a weight that can
     * carry text. Mint 600 as a label on mint 50 is 2.16:1; the text form is
     * 5.14:1 on the same ground.
     */
    color: colors.feedback.success.fg,
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.04em',
    borderRadius: 20,
    padding: '2px 10px',
  },
  notStartedDuration: {
    /*
     * On `gradient.primary`, so it takes that gradient's `on` value. The
     * supporting-text tier is chosen against flat surfaces and measures 3.59:1
     * at the gradient's turquoise end; `on` is 7.26:1 at its worst point.
     */
    color: colors.gradient.primary.on,
    fontSize: '0.78rem',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  notStartedTitle: {
    margin: '4px 0 0',
    fontWeight: 700,
    fontSize: '1.2rem',
    color: colors.brand.onPrimary,
    lineHeight: 1.3,
    '& em': {
      fontStyle: 'italic',
      fontWeight: 700,
    },
  },
  notStartedSubtitle: {
    margin: 0,
    fontSize: '0.85rem',
    /* Same gradient, same reasoning as `notStartedDuration`. */
    color: colors.gradient.primary.on,
    lineHeight: 1.4,
  },
  notStartedCta: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
    fontWeight: 700,
    fontSize: '0.9rem',
    color: colors.text.primary,
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconContainerPending: {
    backgroundColor: colors.brand.accentSubtle,
  },
  iconContainerReviewed: {
    backgroundColor: colors.brand.accentSubtle,
  },
  icon: {
    fontSize: 22,
    color: colors.brand.accentStrong,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontWeight: 700,
    fontSize: '1rem',
    color: colors.text.primary,
    lineHeight: 1.3,
  },
  subtitle: {
    margin: '2px 0 0',
    fontSize: '0.82rem',
    color: colors.text.tertiary,
    lineHeight: 1.3,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  arrow: {
    color: colors.text.disabled,
    flexShrink: 0,
  },
});

export type MedicalProfileState = 'not_started' | 'in_progress' | 'pending_review' | 'in_review' | 'reviewed';

export function getMedicalProfileState(response: QuestionnaireResponseDTO | null | undefined): MedicalProfileState {
  if (!response) return 'not_started';
  if (!response.is_complete) return 'in_progress';
  if (response.reviewed_at) return 'reviewed';
  if (response.review_started_at) return 'in_review';
  return 'pending_review';
}

interface MedicalProfileCardProps {
  response: QuestionnaireResponseDTO | null | undefined;
}

export default function MedicalProfileCard({ response }: MedicalProfileCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles();

  const state = getMedicalProfileState(response);

  const navigateToProfile = () => {
    posthog.capture('medical_profile.card_clicked', { card_state: state });
    navigate(routes.medicalProfile.route);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => e.key === 'Enter' && navigateToProfile();

  if (state === 'pending_review') {
    return (
      <ModuleCard
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.row}>
          <div className={`${styles.iconContainer} ${styles.iconContainerPending}`}>
            <ClockCircleOutlined className={styles.icon} />
          </div>
          <div className={styles.textContainer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p className={styles.title}>{t('medicalProfile.homepage.pendingReview')}</p>
              <Tag color={colors.brand.accentStrong}>
                {t('medicalProfile.homepage.pendingReviewBadge')}
                <CheckOutlined style={{ marginLeft: 4 }} />
              </Tag>
            </div>
            <p className={styles.subtitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {t('medicalProfile.homepage.pendingReviewSubtitle')}
            </p>
          </div>
          <RightOutlined className={styles.arrow} />
        </div>
      </ModuleCard>
    );
  }

  if (state === 'in_review') {
    return (
      <ModuleCard
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.row}>
          <div className={`${styles.iconContainer} ${styles.iconContainerPending}`}>
            <FileTextOutlined className={styles.icon} />
          </div>
          <div className={styles.textContainer}>
            <p className={styles.title}>{t('medicalProfile.homepage.inReview')}</p>
            <p className={styles.subtitle}>{t('medicalProfile.homepage.inReviewSubtitle')}</p>
          </div>
          <RightOutlined className={styles.arrow} />
        </div>
      </ModuleCard>
    );
  }

  if (state === 'reviewed') {
    return (
      <ModuleCard
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.row}>
          <div className={`${styles.iconContainer} ${styles.iconContainerReviewed}`}>
            <FileTextOutlined className={styles.icon} />
          </div>
          <div className={styles.textContainer}>
            <p className={styles.title}>{t('medicalProfile.homepage.reviewed')}</p>
            <p className={styles.subtitle}>{t('medicalProfile.homepage.viewProfile')}</p>
          </div>
          <RightOutlined className={styles.arrow} />
        </div>
      </ModuleCard>
    );
  }

  if (state === 'not_started') {
    return (
      <ModuleCard
        accentBackground
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        title={<span style={{ color: colors.brand.onPrimary }}>{t('medicalProfile.homepage.notStartedTitle')}</span>}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.notStartedContent}>
          <div className={styles.notStartedHeader}>
            <div className={styles.notStartedIconContainer}>
              <FileTextOutlined className={styles.notStartedIconSize} />
            </div>
            <div className={styles.notStartedBadgesRow}>
              <span className={styles.notStartedBadge}>{t('medicalProfile.homepage.notStartedBadge')}</span>
              <span className={styles.notStartedDuration}>
                <ClockCircleOutlined />
                {t('medicalProfile.homepage.duration')}
              </span>
            </div>
          </div>
          <p className={styles.notStartedSubtitle}>{t('medicalProfile.homepage.notStartedSubtitle')}</p>
          <Button
            variant="primary"
            size="lg"
            onClick={navigateToProfile}
            /*
             * An opaque plate, not a tint of the foreground.
             *
             * This button sits on `gradient.brand`, which spans nearly the full
             * lightness range — no single label colour is readable across it
             * (see the note on that token). A 10% wash of `onAccent` let the
             * gradient through, so when the identity swapped primary and
             * accent, `onAccent` flipped from teal to white and the label
             * landed at 1.8:1 over the turquoise stop. An opaque surface makes
             * the pairing independent of whatever is behind it.
             */
            style={{ background: colors.brand.primarySubtle, color: colors.brand.onPrimary }}
          >
            {t('medicalProfile.homepage.notStartedCta')}
          </Button>
        </div>
      </ModuleCard>
    );
  }

  // in_progress — show CTA
  return (
    <ModuleCard
      accentBackground
      style={{ cursor: 'pointer' }}
      onClick={navigateToProfile}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.row}>
        <div className={styles.iconContainer}>
          <FileTextOutlined style={{ fontSize: 22, color: colors.brand.onPrimary }} />
        </div>
        <div className={styles.textContainer}>
          <p className={`${styles.title}`} style={{ color: colors.brand.onPrimary }}>
            {t('medicalProfile.homepage.cta')}
          </p>
          <p className={styles.subtitle} style={{ color: colors.brand.onPrimary }}>
            {t('medicalProfile.homepage.ctaDescription')}
          </p>
        </div>
        <RightOutlined style={{ color: colors.brand.onPrimary, flexShrink: 0 }} />
      </div>
    </ModuleCard>
  );
}
